import React, { useEffect, useRef, useState } from "react";

export function CatalogTableToolbar({
  title,
  rowCount = 0,
  selectedCount = 0,
  allVisibleSelected = false,
  exportStatus,
  customActions = [],
  filteredExportAvailable = false,
  filteredExportUnavailableReason = "This table does not have filters.",
  exportSupported = true,
  loading = false,
  onClearSelection,
  onExportCurrent,
  onExportFiltered,
  onExportSelected,
  onSelectVisible,
}) {
  const hasRows = rowCount > 0;
  const isExporting = Boolean(exportStatus?.loading);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [menuOpen]);

  const getExportOptionReason = (option) => {
    if (isExporting) {
      return "An export is already in progress.";
    }

    if (option === "current" && !hasRows) {
      return "There are no rows on this page to export.";
    }

    if (option === "selected" && selectedCount === 0) {
      return "Select one or more rows to export.";
    }

    if (option === "filtered" && !filteredExportAvailable) {
      return filteredExportUnavailableReason;
    }

    return "";
  };

  const exportOptions = [
    {
      action: onExportCurrent,
      description: "Export the rows currently shown in this table.",
      id: "current",
      label: "Current page",
    },
    {
      action: onExportSelected,
      description: selectedCount > 0 ? `Export ${selectedCount} selected row${selectedCount === 1 ? "" : "s"}.` : "Select rows in the table first.",
      id: "selected",
      label: "Selected rows",
    },
    {
      action: onExportFiltered,
      description: "Export all rows matching the active filters.",
      id: "filtered",
      label: "Filtered results",
    },
  ].map((option) => {
    const unavailableReason = getExportOptionReason(option.id);

    return {
      ...option,
      disabled: Boolean(unavailableReason),
      unavailableReason,
    };
  });

  const handleExportOptionClick = (option) => {
    if (option.disabled || !option.action) {
      return;
    }

    setMenuOpen(false);
    option.action();
  };

  return (
    <div className="catalog-table-toolbar">
      <div className="catalog-table-toolbar-main">
        <div className="catalog-table-heading">
          <span className="catalog-table-title">{title}</span>
          <span className="catalog-table-meta">{rowCount} rows</span>
          {selectedCount > 0 && <span className="catalog-table-meta">{selectedCount} selected</span>}
          {loading && <span className="catalog-table-meta">Loading</span>}
        </div>
      </div>

      <div className="catalog-table-actions">
        {customActions.map((action) => (
          <button
            className="btn btn-sm catalog-toolbar-button"
            disabled={Boolean(action.disabled)}
            key={action.id}
            onClick={action.onClick}
            title={action.title || ""}
            type="button"
          >
            {action.label}
          </button>
        ))}
        {onSelectVisible && (
          <button
            className="btn btn-sm catalog-toolbar-button"
            disabled={!hasRows || isExporting}
            onClick={() => onSelectVisible(!allVisibleSelected)}
            type="button"
          >
            {allVisibleSelected ? "Clear page" : "Select page"}
          </button>
        )}
        {onClearSelection && (
          <button
            className="btn btn-sm catalog-toolbar-button"
            disabled={selectedCount === 0 || isExporting}
            onClick={onClearSelection}
            type="button"
          >
            Clear selection
          </button>
        )}
        {exportSupported ? (
          <div className="catalog-export-menu" ref={menuRef}>
            <button
              aria-expanded={menuOpen}
              aria-haspopup="menu"
              aria-label={`${title} export actions`}
              className="btn btn-sm btn-primary catalog-toolbar-button"
              onClick={() => setMenuOpen((isOpen) => !isOpen)}
              type="button"
            >
              {isExporting ? "Preparing..." : "Export..."}
            </button>

            {menuOpen && (
              <div className="catalog-export-menu-panel" role="menu">
                {exportOptions.map((option) => (
                  <button
                    aria-disabled={option.disabled}
                    className={`catalog-export-menu-option${option.disabled ? " catalog-export-menu-option-disabled" : ""}`}
                    key={option.id}
                    onClick={() => handleExportOptionClick(option)}
                    role="menuitem"
                    title={option.disabled ? option.unavailableReason : option.description}
                    type="button"
                  >
                    <span className="catalog-export-menu-option-title">{option.label}</span>
                    <span className="catalog-export-menu-option-description">
                      {option.unavailableReason || option.description}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <span className="catalog-export-unavailable">Export not available</span>
        )}
      </div>

    </div>
  );
}